import React, { useEffect, useState } from "react";
import CustomAppBar from "../../components/AppBar";
import LoaderContainer from "../../components/LoaderContainer";
import { Button, Container, Grid, Paper, TextField } from "@mui/material";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import GenericButton from "../../components/Custom/GenericButton";
import { useTranslation } from "react-i18next";
import { ContactMail, InsertPhoto } from "@mui/icons-material";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useAlert } from "../../components/Alert";
import api from "../../globals/axios";
import { useNavigate, useParams } from "react-router-dom";
import BiocharOrderDetailCard from "../../components/Cards/BiocharOrderDetailCard";
import { getPickupOrderDetail } from "../../api/biomassProcurement";

const PickupOrderDetail = () => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { id } = useParams();
  const { showAlert } = useAlert();

  const [inwardData, setInwardData] = useState({
    net_inward_weight_kg: "",
    inward_proof: null,
    remarks: "",
    inwarded_date: dayjs(),
    order_id: id,
    quantity_kg: 0,
  });

  const [orderData, setOrderData] = useState({});

  const [previews, setPreviews] = useState({
    inward_proof: null,
  });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await getPickupOrderDetail(id);
        setOrderData(response);
      } catch (error) {
        console.error("Error fetching pickup orders:", error);
        showAlert("error", t("Failed to fetch pickup orders"));
      }
    };
    fetchOrder();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formattedData = {
      ...inwardData,
      inwarded_date: inwardData.inwarded_date
        ? dayjs(inwardData.inwarded_date).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD"),
    };

    try {
      const formData = new FormData();
      Object.keys(formattedData).forEach((key) => {
        if (formattedData[key] !== null && formattedData[key] !== undefined) {
          formData.append(key, formattedData[key]);
        }
      });

      const response = await api.post("/biochar/plant-inward/", formData);

      if (response.status === 200 || response.status === 201) {
        showAlert("success", "Order inwarded successfully!");
        navigate(-1);
      } else {
        showAlert("error", "Something went wrong.");
      }
    } catch (error) {
      showAlert("error", "Error while submitting.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (name, value) => {
    setInwardData({
      ...inwardData,
      [name]: name === "inwarded_date" ? dayjs(value) : value,
    });
  };

  const handleImageUpload = (event, field) => {
    let file = event.target.files[0];
    if (!file) {
      showAlert("error", "Please select a file");
      return;
    }
    file = new File([file], file.name, { type: file.type });
    const previewUrl = URL.createObjectURL(file);
    setPreviews((prev) => ({
      ...prev,
      [field]: previewUrl,
    }));
    setInwardData((prev) => ({
      ...prev,
      [field]: file,
    }));
  };

  return (
    <>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container maxWidth="xs">
          {/* Order Details Card */}
          <BiocharOrderDetailCard orderData={orderData} />
          {/* Inward Form */}
          <Grid
            container
            justify="center"
            direction="column"
            style={{ marginTop: "10px" }}
          >
            <CustomTitle variant="h5">{t("Store Inwards")}</CustomTitle>
            <form onSubmit={handleSubmit} style={{ marginTop: "20px" }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("Net Quantity Inwarded (kg)")}
                    name="net_inward_weight_kg"
                    value={inwardData.net_inward_weight_kg}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("Remarks")}
                    name="remarks"
                    fullWidth
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    value={inwardData.remarks}
                    multiline
                    rows={4}
                  />
                </Grid>
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={["DateTimePicker"]}>
                      <MobileDatePicker
                        label={t("Inwarded Date")}
                        name="inwarded_date"
                        value={
                          inwardData.inwarded_date
                            ? dayjs(inwardData.inwarded_date)
                            : dayjs()
                        }
                        onChange={(value) =>
                          handleChange("inwarded_date", value)
                        }
                        required
                        fullWidth
                      />
                    </DemoContainer>
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <Paper
                    elevation={3}
                    sx={{
                      width: "100%",
                      height: 200,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      mt: 2,
                      position: "relative",
                    }}
                  >
                    {previews.inward_proof ? (
                      <img
                        src={previews.inward_proof || "/placeholder.svg"}
                        alt="Inward Preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                    ) : (
                      <ContactMail sx={{ fontSize: 80, color: "#ccc" }} />
                    )}
                  </Paper>
                  <input
                    id="inward-proof"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => handleImageUpload(e, "inward_proof")}
                  />
                  <Button
                    variant="outlined"
                    component="label"
                    htmlFor="inward-proof"
                    fullWidth
                    sx={{ color: "#4CAF50" }}
                  >
                    <InsertPhoto
                      fontSize="medium"
                      sx={{ mr: 2, color: "#4CAF50" }}
                    />
                    {t("Upload Inward Proof")}
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <GenericButton
                    type="submit"
                    backgroundColor="primary"
                    fontColor={"#fff"}
                    fontSize={"14px"}
                    padding={"7px 10px"}
                  >
                    {t("Accept")}
                  </GenericButton>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Container>
      </LoaderContainer>
    </>
  );
};

export default PickupOrderDetail;
